import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { QuoteModel } from '@data-layer/models/lcl';
import { UntypedFormGroup } from '@angular/forms';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { CompaniesService } from '@services/companies.service';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { CompanyModel } from '@data-layer/models/companies';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { enumMarineInsuranceEntityType } from '@app/data-layer/enums/marine-insurance';

@Component({
  selector: 'app-lcl-edit-additional-options',
  templateUrl: './lcl-edit-additional-options.component.html',
  styleUrls: ['./lcl-edit-additional-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEditAdditionalOptionsComponent implements OnChanges, OnInit {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public quote: QuoteModel;
  @Input() public isManagementView: boolean;
  @Input() public marineInsuranceRequested: boolean;
  @Input() public quoteCompany: CompanyModel;
  @Input() public isInsuranceAvailable: boolean;
  @Input() public isInsuranceLoading: boolean;
  @Input() public currentInsurance: MarineInsuranceModel;
  @Input() public isVoyageValidForInsurance: boolean;
  @Output() public optionISFUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public optionESUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public optionExportLicenseUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public optionMarineInsuranceUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  public enumIncotermsType: typeof enumIncotermsType = enumIncotermsType;
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumMarineInsuranceEntityType: typeof enumMarineInsuranceEntityType = enumMarineInsuranceEntityType;

  public showDeleteQuoteModal: boolean;
  public isCancelClicked: boolean;

  constructor(private companiesService: CompaniesService) {}

  public ngOnInit(): void {
    this.formGroup.get('marineInsuranceRequested').setValue(this.marineInsuranceRequested);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.formGroup.get('marineInsuranceRequested').setValue(this.marineInsuranceRequested);
    }
  }

  public onCheckedExportLicense(event: any): void {
    this.optionExportLicenseUpdated.emit(event.target.checked);
  }

  public onCheckedISF(event: any): void {
    this.optionISFUpdated.emit(event.target.checked);
  }

  public get isOptionISFChecked(): boolean {
    return this.quote.isfRequested;
  }

  public get isOptionESChecked(): boolean {
    return this.quote.entrySummaryRequested;
  }

  public onCheckedES(event: any): void {
    this.optionESUpdated.emit(event.target.checked);
  }

  public onCheckedMarineInsurance(event: any): void {
    const value: boolean = event.target.checked;

    if (!this.formGroup.get('marineInsuranceRequested').value) {
      this.formGroup.get('marineInsuranceRequested').setValue(true);
      this.optionMarineInsuranceUpdated.emit(value);
    } else {
      this.showDeleteQuoteModal = true;
    }
  }

  public discardDeleteQuote(): void {
    this.isCancelClicked = true;
    this.showDeleteQuoteModal = false;
    this.marineInsuranceRequested = true;
    this.formGroup.get('marineInsuranceRequested').setValue(true);
  }

  public confirmDeleteQuote(): void {
    this.isCancelClicked = false;
    this.optionMarineInsuranceUpdated.emit(false);
    this.showDeleteQuoteModal = false;
    this.marineInsuranceRequested = false;
  }

  public get isOptionExportLicenseChecked(): boolean {
    return this.quote.exportLicenseRequested;
  }

  public get isCompanyFromUSA(): boolean {
    const company: CompanyModel = this.isManagementView ? this.quoteCompany : this.companiesService.getCompany();
    // #8011
    return company?.address?.country?.code === USA_COUNTRY_CODE || company?.isUnitedStates || company?.isAllowedForCustoms;
  }

  public get isDestinationUSA(): boolean {
    // #8646
    return this.quote.voyage?.route?.arrivalPort?.country?.code === USA_COUNTRY_CODE;
  }
}
